import React from 'react';

import {PopQuizCompletedQuizzesQuery} from 'src/generated/graphql';
import {POP_QUIZ_SETTINGS, PopQuizSetting} from 'src/lib/pop-quiz/pop-quiz-settings';

import {
    LONG_SNOOZE_DAYS,
    MS_PER_DAY,
    STALE_COURSE_DAYS,
    UDEMY_LOGO_CDN_URL,
    WEEKLY_CAP_MS,
} from './constants';
import {QuizData} from './types';

type LogoProps = {
    src?: string;
    [key: string]: unknown;
};

export function renderUdemyLogo(
    Component: React.ComponentType<LogoProps>,
    props: LogoProps,
): React.ReactElement {
    return <Component {...props} src={UDEMY_LOGO_CDN_URL} />;
}

/**
 * Returns true when the pop quiz should NOT be shown — either because the
 * long snooze is still active or the weekly frequency cap has been reached.
 */
export function isCapped(getPopQuizSetting: (s: PopQuizSetting) => string): boolean {
    if (typeof window === 'undefined') {
        // Settings are not available server-side — treat as capped so the query stays client-only.
        return true;
    }
    // Long snooze takes priority: check if snoozedAt + 60 days is still in the future
    const snoozedAt = getPopQuizSetting(POP_QUIZ_SETTINGS.snoozedAt);
    if (snoozedAt) {
        const snoozeExpiresAt = new Date(snoozedAt).getTime() + LONG_SNOOZE_DAYS * MS_PER_DAY;
        if (Date.now() < snoozeExpiresAt) {
            return true;
        }
    }
    // Fall through to weekly cap
    const lastShown = getPopQuizSetting(POP_QUIZ_SETTINGS.lastShownDate);
    if (!lastShown) {
        return false;
    }
    return Date.now() - new Date(lastShown).getTime() < WEEKLY_CAP_MS;
}

/**
 * Returns the first eligible quiz from `data`, where eligibility requires:
 * - the course was not accessed today (content is still fresh)
 * - the quiz ID was not the last one we already showed (stored in localStorage)
 *
 * Enrollments are pre-sorted by last access time by the GQL query, so the
 * first match is the most recently accessed eligible quiz.
 */
export function findLatestCompletedQuiz(
    data: PopQuizCompletedQuizzesQuery | undefined,
    getPopQuizSetting: (s: PopQuizSetting) => string,
    force = false,
): QuizData | null {
    const enrollments = data?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments;
    if (!enrollments) {
        return null;
    }

    const lastShownQuizId = force ? '' : getPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId);
    const todayDate = new Date().toDateString();

    for (const enrollment of enrollments) {
        if (!enrollment) {
            continue;
        }

        if (!force && enrollment.lastAccessedTime) {
            const daysSinceAccess =
                (Date.now() - new Date(enrollment.lastAccessedTime).getTime()) / MS_PER_DAY;

            // Skip courses accessed very recently — user has fresh context and shouldn't be interrupted
            if (new Date(enrollment.lastAccessedTime).toDateString() === todayDate) {
                continue;
            }

            // Skip courses not accessed within the staleness window — recall is unlikely to be strong
            if (daysSinceAccess > STALE_COURSE_DAYS) {
                continue;
            }
        }

        const completedItems = enrollment.completedItems;
        if (!completedItems) {
            continue;
        }
        for (const item of completedItems) {
            if (item?.__typename === 'Quiz' && item.id && item.id !== lastShownQuizId) {
                const course = enrollment.course;
                if (!course?.urlCourseTaking) {
                    continue;
                }
                return {
                    quizId: Number(item.id),
                    quizTitle: item.title,
                    courseId: Number(course.id),
                    courseTitle: course.title,
                    courseUrl: String(course.urlCourseTaking),
                    courseProgress: Math.round(Number(enrollment.completionPercentage) || 0),
                };
            }
        }
    }
    return null;
}
